import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag } from '@fortawesome/free-solid-svg-icons';
import { 
  EmptyStateMessage,
  Tile,
  ContentErrorBlock,
} from './Tile';
import {
  CodesList,
  CodeItem,
} from './CodeItem';
import {
  CodeSuggestGroupHeader,
  PotentialPreExistingSectionHeader,
  getCodeSuggestCodeIds,
  buildExistingCodeById,
  resolveOverlap,
  isManualSearchGroup,
  isFrequentCodesGroup,
  FREQUENT_CODES_PHRASE,
} from './codeSuggest';
import { ContentType, shouldShowContent, EXPECTED_EXISTING_CODES_ERRORS, existingCodesEmptyMessage } from './contentTypes';
import SearchBar from './SearchBar';
import { DocumentService, sharedApiClient } from '../../fetcher';
import { useAuth, useNotifications, CustomDropdown } from '@medprompt/shared';
import { UserAbility } from '@medprompt/shared';
import { Significance, getNextSignificance, getSignificanceConfig } from '../../enums/significanceEnum';
import { getDurationDescription, getDurationOptions } from '../../enums/codeDurationEnum';

const DataTab = ({ doc, isRegenerationEnabled, isEditingEnabled, handleRegenerationClick, onPdfSearch, onDocumentUpdate, section, highlightedExistingCodeId = null }) => {
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const hasDocsWriteAbility = user?.authorities?.includes(UserAbility.DOCS_WRITE.key);
  const canEdit = isEditingEnabled ? isEditingEnabled() : hasDocsWriteAbility;

  // State for description editing
  const [editingCodeId, setEditingCodeId] = useState(null);
  const [editableDescription, setEditableDescription] = useState('');
  
  // State for duration dropdowns
  const [durationDropdownOpen, setDurationDropdownOpen] = useState({});

  // Description editing handlers
  const handleDescriptionEditToggle = useCallback((codeId, currentDescription) => {
    if (editingCodeId === codeId) {
      setEditingCodeId(null);
      setEditableDescription('');
    } else {
      setEditingCodeId(codeId);
      setEditableDescription(currentDescription || '');
    }
  }, [editingCodeId]);

  const handleDescriptionSave = useCallback(async (codeId, isNewCode = false, originalDescription = '') => {
    if (!canEdit) return;
    
    // Check if there are any changes
    const hasChanges = editableDescription !== (originalDescription || '');
    if (!hasChanges) {
      setEditingCodeId(null);
      setEditableDescription('');
      return;
    }

    const isFrequentCode = doc.frequentCodes?.some((code) => code.codeId === codeId);
    
    try {
      if (isFrequentCode) {
        const updatedFrequentCodes = doc.frequentCodes?.map((code) =>
          code.codeId === codeId ? { ...code, description: editableDescription } : code
        );

        if (onDocumentUpdate) {
          onDocumentUpdate({ ...doc, frequentCodes: updatedFrequentCodes });
        }

        await documentManager.updateDocumentFrequentCode(doc.documentId, codeId, { description: editableDescription });
      } else if (isNewCode) {
        // Update new code description
        const updatedNewCodeSuggests = doc.newCodeSuggests?.map(codeSuggest => ({
          ...codeSuggest,
          suggestions: codeSuggest.suggestions?.map(suggestion => 
            suggestion.codeId === codeId ? { ...suggestion, description: editableDescription } : suggestion
          )
        }));
        
        const updatedDoc = { 
          ...doc, 
          newCodeSuggests: updatedNewCodeSuggests
        };
        
        if (onDocumentUpdate) {
          onDocumentUpdate(updatedDoc);
        }
        
        await documentManager.updateDocumentNewCode(doc.documentId, codeId, { description: editableDescription });
      } else {
        // Update existing code description
        const updateExistingCodes = (codes) => {
          return codes?.map(codeWrapper => 
            codeWrapper.codeId === codeId ? { ...codeWrapper, description: editableDescription } : codeWrapper
          );
        };

        const updatedActiveCodes = updateExistingCodes(doc.existingPatientCodes?.activeCodes);
        const updatedPastCodes = updateExistingCodes(doc.existingPatientCodes?.significantPastCodes);
        
        const updatedDoc = { 
          ...doc, 
          existingPatientCodes: {
            ...doc.existingPatientCodes,
            activeCodes: updatedActiveCodes,
            significantPastCodes: updatedPastCodes
          }
        };
        
        if (onDocumentUpdate) {
          onDocumentUpdate(updatedDoc);
        }
        
        await documentManager.updateDocumentExistingCode(doc.documentId, codeId, { description: editableDescription });
      }
      
      setEditingCodeId(null);
      setEditableDescription('');
    } catch (error) {
      console.error('Error updating code description:', error);
      addNotification(`code-description-error-${codeId}`, 'Failed to update code description', { isError: true });
    }
  }, [canEdit, doc, editableDescription, onDocumentUpdate, documentManager, addNotification]);

  // Handle adding a code to existing patient codes
  const handleAddExistingCode = useCallback(async (existingCode, category) => {
    if (!canEdit) return;
    try {
      const codeToAdd = {
        activeCodes: [],
        significantPastCodes: []
      };

      // Add the ExistingCode to the appropriate category
      if (category === 'Active') {
        codeToAdd.activeCodes.push(existingCode);
      } else {
        codeToAdd.significantPastCodes.push(existingCode);
      }
      
      await documentManager.addExistingPatientCodes(doc.documentId, codeToAdd);
      console.log(`Added ${category.toLowerCase()} code:`, existingCode);
      // SSE callback will automatically update the document with the new code
    } catch (error) {
      console.error('Error adding existing patient code:', error);
    }
  }, [canEdit, doc.documentId, documentManager]);

  const handleAddNewCode = useCallback(async (code) => {
    if (!canEdit) return;
    try {
      await documentManager.addNewPatientCodes(doc.documentId, [code]);
      console.log(`Added new code:`, code);
      // SSE callback will automatically update the document with the new code
    } catch (error) {
      console.error('Error adding new patient code:', error);
    }
  }, [canEdit, doc.documentId, documentManager]);

  // Search handlers for each search bar
  const handleExistingCodesSearch = useCallback(async (query, callback) => {
    try {
      const results = await documentManager.searchDocumentCodes(doc.documentId, query);
      callback(results);
    } catch (error) {
      console.error('Error searching document codes:', error);
      callback([]);
    }
  }, [doc.documentId, documentManager]);

  const handleNewCodesSearch = useCallback(async (query, callback) => {
    try {
      const results = await documentManager.searchAllCodes(query);
      callback(results);
    } catch (error) {
      console.error('Error searching all codes:', error);
      callback([]);
    }
  }, [documentManager]);

  // Handler for existing code selection change
  const handleExistingCodeSelectionChange = useCallback((codeId, isChecked) => {
    console.log(`Existing code ${codeId} selection changed to ${isChecked}`);
    
    // Update local doc state
    const updateExistingCodes = (codes) => {
      return codes?.map(codeWrapper => 
        codeWrapper.codeId === codeId ? { ...codeWrapper, isSelected: isChecked } : codeWrapper
      );
    };

    const updatedActiveCodes = updateExistingCodes(doc.existingPatientCodes?.activeCodes);
    const updatedPastCodes = updateExistingCodes(doc.existingPatientCodes?.significantPastCodes);
    
    const updatedDoc = { 
      ...doc, 
      existingPatientCodes: {
        ...doc.existingPatientCodes,
        activeCodes: updatedActiveCodes,
        significantPastCodes: updatedPastCodes
      }
    };
    
    // Notify parent component of the update
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    // Send the selection state to the backend
    const isSelected = isChecked ? true : null;
    const isDeselected = !isChecked ? true : null;
    documentManager.updateDocumentExistingCode(doc.documentId, codeId, { isSelected, isDeselected });
  }, [doc, onDocumentUpdate, documentManager]);

  // Handler for new code selection change
  const handleNewCodeSelectionChange = useCallback((codeId, isChecked) => {
    console.log(`New code ${codeId} selection changed to ${isChecked}`);
    const isSelected = isChecked ? true : null;
    const isDeselected = !isChecked ? true : null;

    if (doc.frequentCodes?.some((code) => code.codeId === codeId)) {
      const updatedFrequentCodes = doc.frequentCodes.map((code) =>
        code.codeId === codeId ? { ...code, isSelected: isChecked } : code
      );
      if (onDocumentUpdate) {
        onDocumentUpdate({ ...doc, frequentCodes: updatedFrequentCodes });
      }
      documentManager.updateDocumentFrequentCode(doc.documentId, codeId, { isSelected, isDeselected });
      return;
    }
    
    // Update local doc state - traverse through newCodeSuggests and their suggestions
    const updatedNewCodeSuggests = doc.newCodeSuggests?.map(codeSuggest => ({
      ...codeSuggest,
      suggestions: codeSuggest.suggestions?.map(suggestion => 
        suggestion.codeId === codeId ? { ...suggestion, isSelected: isChecked } : suggestion
      )
    }));
    
    const updatedDoc = { 
      ...doc, 
      newCodeSuggests: updatedNewCodeSuggests
    };
    
    // Notify parent component of the update
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    documentManager.updateDocumentNewCode(doc.documentId, codeId, { isSelected, isDeselected });
  }, [doc, onDocumentUpdate, documentManager]);

  // Handler for existing code significance change
  const handleExistingCodeSignificanceChange = useCallback((codeId, newSignificance) => {
    console.log(`Existing code ${codeId} significance changed to ${newSignificance}`);
    
    // Update local doc state
    const updateExistingCodes = (codes) => {
      return codes?.map(codeWrapper => 
        codeWrapper.codeId === codeId ? { ...codeWrapper, significance: newSignificance } : codeWrapper
      );
    };

    const updatedActiveCodes = updateExistingCodes(doc.existingPatientCodes?.activeCodes);
    const updatedPastCodes = updateExistingCodes(doc.existingPatientCodes?.significantPastCodes);
    
    const updatedDoc = { 
      ...doc, 
      existingPatientCodes: {
        ...doc.existingPatientCodes,
        activeCodes: updatedActiveCodes,
        significantPastCodes: updatedPastCodes
      }
    };
    
    // Notify parent component of the update
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    // Send the significance change to the backend
    documentManager.updateDocumentExistingCode(doc.documentId, codeId, { significance: newSignificance });
  }, [doc, onDocumentUpdate, documentManager]);

  // Handler for new code significance change
  const handleNewCodeSignificanceChange = useCallback((codeId, newSignificance) => {
    console.log(`New code ${codeId} significance changed to ${newSignificance}`);

    if (doc.frequentCodes?.some((code) => code.codeId === codeId)) {
      const updatedFrequentCodes = doc.frequentCodes.map((code) =>
        code.codeId === codeId ? { ...code, significance: newSignificance } : code
      );
      if (onDocumentUpdate) {
        onDocumentUpdate({ ...doc, frequentCodes: updatedFrequentCodes });
      }
      documentManager.updateDocumentFrequentCode(doc.documentId, codeId, { significance: newSignificance });
      return;
    }
    
    // Update local doc state - traverse through newCodeSuggests and their suggestions
    const updatedNewCodeSuggests = doc.newCodeSuggests?.map(codeSuggest => ({
      ...codeSuggest,
      suggestions: codeSuggest.suggestions?.map(suggestion => 
        suggestion.codeId === codeId ? { ...suggestion, significance: newSignificance } : suggestion
      )
    }));
    
    const updatedDoc = { 
      ...doc, 
      newCodeSuggests: updatedNewCodeSuggests
    };
    
    // Notify parent component of the update
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    // Send the significance change to the backend
    documentManager.updateDocumentNewCode(doc.documentId, codeId, { significance: newSignificance });
  }, [doc, onDocumentUpdate, documentManager]);

  // Handler for existing code duration change
  const handleExistingCodeDurationChange = useCallback((codeId, newDuration) => {
    console.log(`Existing code ${codeId} duration changed to ${newDuration}`);
    
    // Update local doc state
    const updateExistingCodes = (codes) => {
      return codes?.map(codeWrapper => 
        codeWrapper.codeId === codeId ? { ...codeWrapper, duration: newDuration } : codeWrapper
      );
    };

    const updatedActiveCodes = updateExistingCodes(doc.existingPatientCodes?.activeCodes);
    const updatedPastCodes = updateExistingCodes(doc.existingPatientCodes?.significantPastCodes);
    
    const updatedDoc = { 
      ...doc, 
      existingPatientCodes: {
        ...doc.existingPatientCodes,
        activeCodes: updatedActiveCodes,
        significantPastCodes: updatedPastCodes
      }
    };
    
    // Notify parent component of the update
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    // Send the duration change to the backend
    documentManager.updateDocumentExistingCode(doc.documentId, codeId, { duration: newDuration });
    
    // Close the dropdown
    setDurationDropdownOpen((prev) => ({ ...prev, [codeId]: false }));
  }, [doc, onDocumentUpdate, documentManager]);

  // Handler for new code date change
  const handleNewCodeDateChange = useCallback((codeId, epochMillis) => {
    if (doc.frequentCodes?.some((code) => code.codeId === codeId)) {
      const updatedFrequentCodes = doc.frequentCodes.map((code) =>
        code.codeId === codeId ? { ...code, date: epochMillis } : code
      );
      if (onDocumentUpdate) {
        onDocumentUpdate({ ...doc, frequentCodes: updatedFrequentCodes });
      }
      documentManager.updateDocumentFrequentCode(doc.documentId, codeId, { date: epochMillis });
      return;
    }

    const updatedNewCodeSuggests = doc.newCodeSuggests?.map(codeSuggest => ({
      ...codeSuggest,
      suggestions: codeSuggest.suggestions?.map(suggestion =>
        suggestion.codeId === codeId ? { ...suggestion, date: epochMillis } : suggestion
      )
    }));

    const updatedDoc = {
      ...doc,
      newCodeSuggests: updatedNewCodeSuggests
    };

    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }

    documentManager.updateDocumentNewCode(doc.documentId, codeId, { date: epochMillis });
  }, [doc, onDocumentUpdate, documentManager]);

  // Handler for new code duration change
  const handleNewCodeDurationChange = useCallback((codeId, newDuration) => {
    console.log(`New code ${codeId} duration changed to ${newDuration}`);

    if (doc.frequentCodes?.some((code) => code.codeId === codeId)) {
      const updatedFrequentCodes = doc.frequentCodes.map((code) =>
        code.codeId === codeId ? { ...code, duration: newDuration } : code
      );
      if (onDocumentUpdate) {
        onDocumentUpdate({ ...doc, frequentCodes: updatedFrequentCodes });
      }
      documentManager.updateDocumentFrequentCode(doc.documentId, codeId, { duration: newDuration });
      setDurationDropdownOpen((prev) => ({ ...prev, [codeId]: false }));
      return;
    }
    
    // Update local doc state - traverse through newCodeSuggests and their suggestions
    const updatedNewCodeSuggests = doc.newCodeSuggests?.map(codeSuggest => ({
      ...codeSuggest,
      suggestions: codeSuggest.suggestions?.map(suggestion => 
        suggestion.codeId === codeId ? { ...suggestion, duration: newDuration } : suggestion
      )
    }));
    
    const updatedDoc = { 
      ...doc, 
      newCodeSuggests: updatedNewCodeSuggests
    };
    
    // Notify parent component of the update
    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }
    
    // Send the duration change to the backend
    documentManager.updateDocumentNewCode(doc.documentId, codeId, { duration: newDuration });
    
    // Close the dropdown
    setDurationDropdownOpen((prev) => ({ ...prev, [codeId]: false }));
  }, [doc, onDocumentUpdate, documentManager]);

  // Toggle function for duration dropdown
  const toggleDurationDropdown = useCallback((codeId) => {
    setDurationDropdownOpen((prev) => ({
      ...prev,
      [codeId]: !prev[codeId],
    }));
  }, []);

  const clearEditingStateForCode = useCallback((codeId) => {
    if (editingCodeId === codeId) {
      setEditingCodeId(null);
      setEditableDescription('');
    }
    setDurationDropdownOpen((prev) => {
      if (!prev[codeId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[codeId];
      return next;
    });
  }, [editingCodeId]);

  const handleDeleteExistingCode = useCallback(async (codeId) => {
    if (!canEdit || !codeId) return;

    const removeCode = (codes) => codes?.filter((codeWrapper) => codeWrapper.codeId !== codeId);

    const updatedActiveCodes = removeCode(doc.existingPatientCodes?.activeCodes);
    const updatedPastCodes = removeCode(doc.existingPatientCodes?.significantPastCodes);

    const updatedDoc = {
      ...doc,
      existingPatientCodes: {
        ...doc.existingPatientCodes,
        activeCodes: updatedActiveCodes,
        significantPastCodes: updatedPastCodes,
      },
    };

    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }

    clearEditingStateForCode(codeId);

    try {
      await documentManager.deleteExistingCodes(doc.documentId, [codeId]);
    } catch (error) {
      console.error('Error deleting existing code:', error);
      addNotification(`code-delete-error-${codeId}`, 'Failed to remove code', { isError: true });
    }
  }, [canEdit, doc, onDocumentUpdate, documentManager, addNotification, clearEditingStateForCode]);

  const handleDeleteNewCodes = useCallback(async (codeIds) => {
    if (!canEdit || !codeIds?.length) return;

    const codeIdSet = new Set(codeIds);
    const updatedNewCodeSuggests = doc.newCodeSuggests
      ?.map((codeSuggest) => ({
        ...codeSuggest,
        suggestions: codeSuggest.suggestions?.filter((suggestion) => !codeIdSet.has(suggestion.codeId)),
      }))
      .filter((codeSuggest) => codeSuggest.suggestions?.length > 0);

    const updatedDoc = {
      ...doc,
      newCodeSuggests: updatedNewCodeSuggests,
    };

    if (onDocumentUpdate) {
      onDocumentUpdate(updatedDoc);
    }

    codeIds.forEach(clearEditingStateForCode);

    try {
      await documentManager.deleteNewCodes(doc.documentId, codeIds);
    } catch (error) {
      console.error('Error deleting new code(s):', error);
      addNotification(`code-delete-error-${doc.documentId}`, 'Failed to remove code(s)', { isError: true });
    }
  }, [canEdit, doc, onDocumentUpdate, documentManager, addNotification, clearEditingStateForCode]);

  const handleDeleteNewCode = useCallback((codeId) => {
    handleDeleteNewCodes([codeId]);
  }, [handleDeleteNewCodes]);

  // Helper function to create code sections
  const createCodesSection = (codes, title, idPrefix, onCodeSelectionChange, onSignificanceChange, onDurationChange, canEditDuration = true, shouldEnableSignificanceToggle = true, description = null, highlightedCodeId = null) => codes && (
    <CodesList 
      codes={codes}
      title={title}
      idPrefix={idPrefix}
      highlightedCodeId={highlightedCodeId}
      onSelectAll={(e) => {
        const checked = e.target.checked;
        // Update state via React - no DOM manipulation needed since checkboxes are controlled
        codes.forEach((codeWrapper) => {
          if (onCodeSelectionChange && codeWrapper.codeId) {
            onCodeSelectionChange(codeWrapper.codeId, checked);
          }
        });
      }}
      onCodeSelectionChange={onCodeSelectionChange}
      onSignificanceChange={onSignificanceChange}
      onDurationChange={onDurationChange}
      onDescriptionEdit={handleDescriptionEditToggle}
      onDescriptionSave={handleDescriptionSave}
      editingCodeId={editingCodeId}
      editableDescription={editableDescription}
      setEditableDescription={setEditableDescription}
      canEdit={canEdit}
      durationDropdownOpen={durationDropdownOpen}
      toggleDurationDropdown={toggleDurationDropdown}
      getDurationDescription={getDurationDescription}
      getDurationOptions={getDurationOptions}
      canEditDuration={canEditDuration}
      shouldEnableSignificanceToggle={shouldEnableSignificanceToggle}
      description={description}
      onQuoteClick={onPdfSearch}
      onDelete={canEdit ? handleDeleteExistingCode : null}
    />
  );

  const emptyCodesMessage = existingCodesEmptyMessage(doc);

  // Extract existing codes section
  const renderExistingCodes = () => {
    if (!doc.existingPatientCodes) {
      return <EmptyStateMessage message={emptyCodesMessage} />;
    }

    const activeCodes = doc.existingPatientCodes.activeCodes;
    const pastCodes = doc.existingPatientCodes.significantPastCodes;

    if (!activeCodes?.length && !pastCodes?.length) {
      return <EmptyStateMessage message={emptyCodesMessage} />;
    }

    const activeCodesSection = createCodesSection(
      activeCodes, 
      "Active Codes", 
      "active", 
      handleExistingCodeSelectionChange, 
      handleExistingCodeSignificanceChange, 
      handleExistingCodeDurationChange, 
      false, 
      false, 
      "Selected active codes are grouped with the existing problem in the patient's record",
      highlightedExistingCodeId
    );
    const pastCodesSection = createCodesSection(
      pastCodes, 
      "Significant Past Codes", 
      "past", 
      handleExistingCodeSelectionChange, 
      handleExistingCodeSignificanceChange, 
      handleExistingCodeDurationChange, 
      true, 
      true, 
      "Selected significant past codes are created as new entries of the existing problem in the patient's record",
      highlightedExistingCodeId
    );

    return (
      <>
        {activeCodesSection}
        {pastCodesSection}
      </>
    );
  };

  // Create a QoF badge component
  const renderQofBadge = () => (
    <span className="qof-badge-inline">
      <FontAwesomeIcon icon={faFlag} />
      <div className="qof-badge-tooltip">This code is part of QOF</div>
    </span>
  );

  // Extract new codes section
  const renderNewCodes = () => {
    const newCodeSuggests = doc.newCodeSuggests || [];
    const frequentCodes = doc.frequentCodes || [];
    const hasFrequentCodes = frequentCodes.length > 0;
    const hasNewCodeSuggests = newCodeSuggests.length > 0;

    if (!hasFrequentCodes && !hasNewCodeSuggests) {
      return (
        <>
          <ContentErrorBlock
            doc={doc}
            contentType={ContentType.FREQUENT_CODES_STATUS}
            isRetryEnabled={isRegenerationEnabled()}
            onRegenerateClick={handleRegenerationClick}
            compact
          />
          <ContentErrorBlock
            doc={doc}
            contentType={ContentType.POTENTIAL_EXISTING_CODES_STATUS}
            isRetryEnabled={isRegenerationEnabled()}
            onRegenerateClick={handleRegenerationClick}
            compact
          />
          <EmptyStateMessage message="No new codes found" />
        </>
      );
    }

    const codesById = buildExistingCodeById(doc.existingPatientCodes);
    const overlapsByIndex = new Map(
      (doc.potentialExistingCodes || []).map((overlap) => [overlap.newSuggestIndex, overlap])
    );

    const manualSearchGroups = [];
    const unflaggedGroups = [];
    const flaggedGroups = [];

    newCodeSuggests.forEach((codeSuggest, originalIndex) => {
      if (isManualSearchGroup(codeSuggest)) {
        manualSearchGroups.push({ codeSuggest, originalIndex });
        return;
      }

      const overlap = resolveOverlap(overlapsByIndex.get(originalIndex), codesById);
      const group = { codeSuggest, originalIndex, overlap };
      if (overlap) {
        flaggedGroups.push(group);
      } else {
        unflaggedGroups.push(group);
      }
    });

    const topGroups = [
      ...manualSearchGroups,
      ...(hasFrequentCodes
        ? [{ codeSuggest: { phrase: FREQUENT_CODES_PHRASE, suggestions: frequentCodes } }]
        : []),
      ...unflaggedGroups,
    ];

    const renderSuggestGroup = (group, index) => {
      const { codeSuggest, overlap, originalIndex } = group;
      const selectedSuggestion = codeSuggest.suggestions.find(s => s.isSelected);
      const hasSelection = !!selectedSuggestion;

      const isManualSearch = isManualSearchGroup(codeSuggest);
      const isFrequentCodes = isFrequentCodesGroup(codeSuggest);
      const keepExpanded = isManualSearch || isFrequentCodes;

      return (
        <div
          key={`${codeSuggest.phrase}-${originalIndex ?? `frequent-${index}`}`}
          className={`new-code-group ${isManualSearch ? 'manual-search-group' : ''} ${isFrequentCodes ? 'frequent-codes-group' : ''} ${overlap ? 'has-overlap' : ''}`}
        >
          <CodeSuggestGroupHeader
            codeSuggest={codeSuggest}
            canEdit={canEdit && !isFrequentCodes}
            onDeleteGroup={isFrequentCodes ? null : handleDeleteNewCodes}
            overlap={overlap}
            onExistingCodeSelect={canEdit ? handleExistingCodeSelectionChange : null}
          />
          <div className="document-codes-list">
            {codeSuggest.suggestions.map((suggestion, suggestionIndex) => {
              const isSelected = suggestion.isSelected;
              const shouldCollapse = !keepExpanded && hasSelection && !isSelected;

              const codeData = {
                codeId: suggestion.codeId,
                code: suggestion.record,
                significance: suggestion.significance || Significance.MAJOR.value,
                description: suggestion.description,
                duration: suggestion.duration,
                date: suggestion.date,
                isSelected: suggestion.isSelected,
                quote: isManualSearch || isFrequentCodes
                  ? suggestion.quote
                  : (suggestion.quote || codeSuggest.phrase)
              };

              const qofContent = suggestion.record.is_qof ? renderQofBadge() : null;

              return (
                <div
                  key={suggestion.codeId || suggestionIndex}
                  className={`code-item-wrapper ${shouldCollapse ? 'collapsed' : 'expanded'}`}
                >
                  <CodeItem
                    code={codeData}
                    index={suggestionIndex}
                    idPrefix={`new_${index}`}
                    onCodeSelectionChange={handleNewCodeSelectionChange}
                    onSignificanceChange={handleNewCodeSignificanceChange}
                    onDurationChange={handleNewCodeDurationChange}
                    onDateChange={canEdit ? handleNewCodeDateChange : null}
                    onDescriptionEdit={handleDescriptionEditToggle}
                    onDescriptionSave={(codeId, isNewCode, originalDescription) =>
                      handleDescriptionSave(codeId, true, originalDescription)
                    }
                    editingCodeId={editingCodeId}
                    editableDescription={editableDescription}
                    setEditableDescription={setEditableDescription}
                    canEdit={canEdit}
                    durationDropdownOpen={durationDropdownOpen}
                    toggleDurationDropdown={toggleDurationDropdown}
                    getDurationDescription={getDurationDescription}
                    getDurationOptions={getDurationOptions}
                    canEditDuration={true}
                    shouldEnableSignificanceToggle={true}
                    qofContent={qofContent}
                    onQuoteClick={isManualSearch ? null : onPdfSearch}
                    onDelete={canEdit && !isFrequentCodes ? handleDeleteNewCode : null}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <>
        {topGroups.filter((group) => !isFrequentCodesGroup(group.codeSuggest)).length > 0 &&
          topGroups
            .filter((group) => !isFrequentCodesGroup(group.codeSuggest))
            .map((group, index) => renderSuggestGroup(group, index))}
        <ContentErrorBlock
          doc={doc}
          contentType={ContentType.FREQUENT_CODES_STATUS}
          isRetryEnabled={isRegenerationEnabled()}
          onRegenerateClick={handleRegenerationClick}
          compact
        />
        {topGroups
          .filter((group) => isFrequentCodesGroup(group.codeSuggest))
          .map((group, index) => renderSuggestGroup(group, `frequent-${index}`))}
        <ContentErrorBlock
          doc={doc}
          contentType={ContentType.POTENTIAL_EXISTING_CODES_STATUS}
          isRetryEnabled={isRegenerationEnabled()}
          onRegenerateClick={handleRegenerationClick}
          compact
        />
        {flaggedGroups.length > 0 && (
          <div className="new-code-overlap-section">
            <PotentialPreExistingSectionHeader
              canEdit={canEdit}
              onDelete={() => {
                const codeIds = flaggedGroups.flatMap(({ codeSuggest }) => getCodeSuggestCodeIds(codeSuggest));
                if (codeIds.length) {
                  handleDeleteNewCodes(codeIds);
                }
              }}
            />
            <div className="new-code-overlap-section-items">
              {flaggedGroups.map((group, index) => renderSuggestGroup(group, `overlap-${index}`))}
            </div>
          </div>
        )}
      </>
    );
  };

  // Create elements for each tile
  const existingCodesTile = (
    <Tile
      doc={doc}
      documentId={doc.documentId} 
      contentType={ContentType.EXISTING_CODES_STATUS} 
      title="Existing Codes"
      condition={shouldShowContent(doc.existingPatientCodes, doc.status)}
      isEnabled={isRegenerationEnabled()} 
      onRegenerateClick={handleRegenerationClick}
      showContentOnError
      shouldShowError={(error) => !EXPECTED_EXISTING_CODES_ERRORS.has(error.message)}
      preContent={doc?.patientId ? (
        <SearchBar
          placeholder="Search patient's existing codes..."
          onSearch={handleExistingCodesSearch}
          onResultClick={(result) => handleAddExistingCode(result, result.category)}
        />
      ) : null}
    >
      <div className="document-codes-list">
        {renderExistingCodes()}
      </div>
    </Tile>
  );

  const newCodesTile = (
    <Tile
      doc={doc}
      documentId={doc.documentId} 
      contentType={ContentType.NEW_CODES_STATUS} 
      title="New Codes"
      condition={shouldShowContent(doc.newCodeSuggests, doc.status)}
      isEnabled={isRegenerationEnabled()} 
      onRegenerateClick={handleRegenerationClick}
      showContentOnError
      preContent={
        <>
          <div className="tile-header-content">
            <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
              Selected new codes are added as new entries to the patient's record
            </p>
          </div>
          <SearchBar
            placeholder="Search SNOMED database for codes..."
            onSearch={handleNewCodesSearch}
            onResultClick={(result) => handleAddNewCode(result)}
          />
        </>
      }
    >
      <div className="document-codes-list">
        {renderNewCodes()}
      </div>
    </Tile>
  );

  const showExisting = section === 'existing';
  const showNew = section === 'new';

  return (
    <div className="document-detail-tiles">
      {showExisting && existingCodesTile}
      {showNew && newCodesTile}
    </div>
  );
};

export default DataTab; 